import React, { useState } from 'react';
import { Star, Send, CheckCircle } from 'lucide-react';
import { api } from '../../config/api';

const LeaveReview = () => {
  const [form, setForm] = useState({ name: '', rating: 0, comment: '' });
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!form.name.trim()) {
      setErrorMsg('Please enter your name.');
      return;
    }
    if (form.rating < 1) {
      setErrorMsg('Please select a rating.');
      return;
    }
    if (!form.comment.trim()) {
      setErrorMsg('Please write your review.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/reviews.php', {
        name: form.name.trim(),
        rating: form.rating,
        comment: form.comment.trim(),
      });
      if (res.data.success) {
        setSuccess(true);
        setForm({ name: '', rating: 0, comment: '' });
        setHoverRating(0);
      } else {
        setErrorMsg(res.data.message || 'Failed to submit review. Please try again.');
      }
    } catch (err) {
      console.error('Review submit error:', err);
      setErrorMsg('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section-padding" style={{ backgroundColor: '#faf8f5' }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-7 reveal">
            <div className="text-center mb-4">
              <span style={{ color: '#D4AF37', letterSpacing: '3px', textTransform: 'uppercase', fontSize: '0.85rem', fontWeight: 600 }}>
                Share Your Experience
              </span>
              <h2 className="mt-2" style={{ fontFamily: 'Playfair Display, serif', fontSize: '2.5rem', fontWeight: 600 }}>
                Leave a Review
              </h2>
              <div className="gold-divider-center"></div>
              <p style={{ fontSize: '0.95rem', color: '#777', marginTop: '12px' }}>
                Your review will appear after admin approval.
              </p>
            </div>

            <div style={{
              backgroundColor: '#ffffff', borderRadius: '16px', padding: '40px',
              boxShadow: '0 4px 25px rgba(0,0,0,0.06)', border: '1px solid rgba(212, 175, 55, 0.1)'
            }}>
              {success ? (
                <div className="text-center py-4">
                  <CheckCircle size={56} color="#4CAF50" style={{ marginBottom: '16px' }} />
                  <h4 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, marginBottom: '8px' }}>
                    Thank you for your review!
                  </h4>
                  <p style={{ color: '#777', marginBottom: '24px' }}>
                    Your review has been submitted and will appear once approved by our team.
                  </p>
                  <button
                    type="button"
                    className="btn btn-gold"
                    style={{ padding: '12px 32px', fontSize: '0.85rem' }}
                    onClick={() => setSuccess(false)}
                  >
                    Write Another Review
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {errorMsg && (
                    <div style={{
                      backgroundColor: 'rgba(231,76,60,0.12)', color: '#C62828',
                      padding: '12px 16px', borderRadius: '10px', marginBottom: '20px',
                      fontSize: '0.9rem', textAlign: 'center'
                    }}>
                      {errorMsg}
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444' }}>
                      Your Name <span style={{ color: '#D4AF37' }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. Sarah Johnson"
                      style={{ padding: '12px 16px' }}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label d-block" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444' }}>
                      Your Rating <span style={{ color: '#D4AF37' }}>*</span>
                    </label>
                    <div className="d-flex gap-2" role="radiogroup" aria-label="Your rating">
                      {[1, 2, 3, 4, 5].map((value) => {
                        const filled = value <= (hoverRating || form.rating);
                        return (
                          <button
                            key={value}
                            type="button"
                            aria-label={`${value} star${value > 1 ? 's' : ''}`}
                            aria-pressed={form.rating === value}
                            onClick={() => setForm({ ...form, rating: value })}
                            onMouseEnter={() => setHoverRating(value)}
                            onMouseLeave={() => setHoverRating(0)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: 0, lineHeight: 0
                            }}
                          >
                            <Star
                              size={32}
                              fill={filled ? '#D4AF37' : 'none'}
                              color="#D4AF37"
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444' }}>
                      Your Review <span style={{ color: '#D4AF37' }}>*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={form.comment}
                      onChange={(e) => setForm({ ...form, comment: e.target.value })}
                      placeholder="Tell us about your experience at Syra Food Point & Grill..."
                      style={{ padding: '12px 16px', resize: 'none' }}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn btn-gold w-100 d-flex align-items-center justify-content-center gap-2"
                    style={{
                      padding: '14px', fontSize: '0.9rem', letterSpacing: '1px',
                      cursor: submitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <Send size={16} /> {submitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeaveReview;
